import React from 'react';
import { motion } from 'framer-motion';
import { Download, Calendar, Clock, FileArchive, CheckCircle, XCircle, Loader2, Eye, EyeOff, RefreshCw, Trash2 } from 'lucide-react';
import DateSelector from './DateSelector';
import { ExecutionStatus, CustomDates, DownloadHistory, SimulationInputConfig } from './types';

interface DownloadSectionProps {
  status: ExecutionStatus;
  dateMode: 'single' | 'range';
  dates: CustomDates;
  onDateModeChange: (mode: 'single' | 'range') => void;
  onDateChange: (dates: CustomDates) => void;
  simulationInputs: SimulationInputConfig;
  onSimulationInputChange: (key: keyof SimulationInputConfig, value: string) => void;
  onBrowseLocalInputPath: () => Promise<void>;
  onBrowseOutputPath: () => Promise<void>;
  onBrowseCwdPath: () => Promise<void>;
  onExecute: () => void;
  onExecuteWebScrape: (plant: string, dateMode: string, manualDate: string, startDate: string, endDate: string) => void;
  onStopExecute?: () => void;
  downloadHistory: DownloadHistory[];
  onRedownload: (item: DownloadHistory) => Promise<void>;
  onDeleteHistory?: (item: DownloadHistory) => Promise<void>;
  onOpenFolder: (path?: string) => Promise<void>;
  actionsDisabled?: boolean;
  tokenCost?: number;
  tokenBalance?: number | null;
}

const DownloadSection: React.FC<DownloadSectionProps> = ({
  status,
  dateMode,
  dates,
  onDateModeChange,
  onDateChange,
  simulationInputs,
  onSimulationInputChange,
  onBrowseLocalInputPath,
  onBrowseOutputPath,
  onBrowseCwdPath,
  onExecute,
  onExecuteWebScrape,
  onStopExecute,
  downloadHistory,
  onRedownload,
  onDeleteHistory,
  onOpenFolder,
  actionsDisabled,
  tokenCost = 1,
  tokenBalance = null
}) => {
  const hasTokens = tokenBalance === null ? true : tokenBalance >= tokenCost;
  const executeDisabled = status.status === 'running' || !!actionsDisabled || !hasTokens;

  const [activeTab, setActiveTab] = React.useState<'azure' | 'web_scrape'>(() => {
    try {
      const stored = localStorage.getItem('simulation_download_active_tab');
      if (stored === 'azure' || stored === 'web_scrape') {
        return stored;
      }
    } catch (e) {}
    return 'azure';
  });

  React.useEffect(() => {
    try {
      localStorage.setItem('simulation_download_active_tab', activeTab);
    } catch (e) {}
  }, [activeTab]);

  // Web scrape form states
  const [selectedPlant, setSelectedPlant] = React.useState<string>('1.DEVIKOT_BHAKRANI_2');
  const [scrapeDateMode, setScrapeDateMode] = React.useState<string>('yesterday_date');
  const [scrapeManualDate, setScrapeManualDate] = React.useState<string>('2026-06-06');
  const [scrapeStartDate, setScrapeStartDate] = React.useState<string>('2026-04-20');
  const [scrapeEndDate, setScrapeEndDate] = React.useState<string>('2026-04-27');

  const [showFields, setShowFields] = React.useState<Record<string, boolean>>({
    localInputPath: false,
    outputPaths: false,
    azureSasKey: false,
    cwd: false
  });

  const toggleField = (field: string) => {
    setShowFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const formatPlantName = (name: string): string => {
    // Remove starting number and dot: "1.DEVIKOT..." -> "DEVIKOT..."
    let cleaned = name.replace(/^\d+\./, '');
    // Remove trailing underscore and number: "..._2" -> "..."
    cleaned = cleaned.replace(/_\d+$/, '');
    // Replace underscores with spaces
    return cleaned.replace(/_/g, ' ');
  };

  const inputClasses = "w-full rounded-lg border border-gray-300 pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0084CF] placeholder:font-light";

  const filteredHistory = React.useMemo(() => {
    return downloadHistory.filter((item) => {
      const isWS =
        item.source === 'web_scrape' ||
        String(item.activityType || '').toLowerCase().includes('web-scrape') ||
        String(item.activityType || '').toLowerCase().includes('web_scrape');
      if (activeTab === 'web_scrape') {
        return isWS;
      }
      return !isWS;
    });
  }, [downloadHistory, activeTab]);

  return (
    <div className="grid grid-cols-[0.9fr_1.1fr] gap-4 items-stretch min-h-[calc(100vh-140px)]">
      {/* Left Column - Download Configuration */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full"
      >
        {/* Card Header */}
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50 flex-shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#0084CF] flex items-center justify-center flex-shrink-0">
              <Download size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-black text-gray-950 tracking-tight" style={{ color: '#16202B', fontWeight: 900 }}>Download Raw Data</h3>
              <p className="text-xs font-black text-gray-500 tracking-tight" style={{ fontWeight: 700 }}>
                {activeTab === 'azure' ? 'Download Raw data from Azure Cloud' : 'Download Raw data via Web Scrape'}
              </p>
            </div>
          </div>
        </div>

        {/* Card Content */}
        <div className="p-4 space-y-4 flex-1 overflow-y-auto">
          {/* Tab Switcher Pills */}
          <div className="flex bg-gray-200/60 p-0.5 rounded-lg border border-gray-300/30 w-full flex-shrink-0">
            <button
              onClick={() => setActiveTab('azure')}
              className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${activeTab === 'azure' ? 'bg-[#0084CF] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              style={{ fontWeight: 800 }}
            >
              Azure Cloud
            </button>
            <button
              onClick={() => setActiveTab('web_scrape')}
              className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${activeTab === 'web_scrape' ? 'bg-[#0084CF] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              style={{ fontWeight: 800 }}
            >
              Web Scrape
            </button>
          </div>

          {activeTab === 'azure' ? (
            <>
              {/* Date Selector */}
              <DateSelector
                mode={dateMode}
                dates={dates}
                onModeChange={onDateModeChange}
                onDateChange={onDateChange}
              />

              {/* Simulation Inputs */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-xs font-extrabold text-gray-900 mb-1" style={{ color: '#16202B', fontWeight: 900 }}>
                    Local Input Path
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showFields.localInputPath ? "text" : "password"}
                        value={simulationInputs.localInputPath}
                        onChange={(e) => onSimulationInputChange('localInputPath', e.target.value)}
                        placeholder="D:/simulation/input"
                        className={inputClasses}
                      />
                      <button
                        type="button"
                        onClick={() => toggleField('localInputPath')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showFields.localInputPath ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={onBrowseLocalInputPath}
                      className="px-3 py-2 bg-gray-50 text-xs font-extrabold text-[#0084CF] border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                      style={{ fontWeight: 1000 }}
                    >
                      Select Folder
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-extrabold text-gray-900 mb-1" style={{ color: '#16202B', fontWeight: 900 }}>
                    Output Path(s) <span className="text-gray-400 font-normal">(optional - defaults to Local Input Path when empty)</span>
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showFields.outputPaths ? "text" : "password"}
                        value={simulationInputs.outputPaths}
                        onChange={(e) => onSimulationInputChange('outputPaths', e.target.value)}
                        placeholder="https://storage/path1, https://storage/path2"
                        className={inputClasses}
                      />
                      <button
                        type="button"
                        onClick={() => toggleField('outputPaths')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showFields.outputPaths ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={onBrowseOutputPath}
                      className="px-3 py-2 bg-gray-50 text-xs font-extrabold text-[#0084CF] border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                      style={{ fontWeight: 1000 }}
                    >
                      Select Folder
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Enter one or more output paths separated by commas.</p>
                </div>

                <div>
                  <label className="block text-xs font-extrabold text-gray-900 mb-1" style={{ color: '#16202B', fontWeight: 900 }}>
                    Azure SAS Key
                  </label>
                  <div className="relative">
                    <input
                      type={showFields.azureSasKey ? "text" : "password"}
                      value={simulationInputs.azureSasKey}
                      onChange={(e) => onSimulationInputChange('azureSasKey', e.target.value)}
                      placeholder="sv=...&sig=..."
                      className={inputClasses}
                    />
                    <button
                      type="button"
                      onClick={() => toggleField('azureSasKey')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showFields.azureSasKey ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-extrabold text-gray-900 mb-1" style={{ color: '#16202B', fontWeight: 900 }}>
                    Working Directory (cwd)
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showFields.cwd ? "text" : "password"}
                        value={simulationInputs.cwd}
                        onChange={(e) => onSimulationInputChange('cwd', e.target.value)}
                        placeholder="D:/tools/azcopy"
                        className={inputClasses}
                      />
                      <button
                        type="button"
                        onClick={() => toggleField('cwd')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showFields.cwd ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={onBrowseCwdPath}
                      className="px-3 py-2 bg-gray-50 text-xs font-extrabold text-[#0084CF] border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                      style={{ fontWeight: 1000 }}
                    >
                      Select Folder
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-5">
              {/* Plant selection dropdown */}
              <div className="space-y-2">
                <label className="block text-sm font-extrabold text-gray-950" style={{ color: '#16202B', fontWeight: 800 }}>
                  Select Target Plant
                </label>
                <select
                  value={selectedPlant}
                  onChange={(e) => setSelectedPlant(e.target.value)}
                  className="w-full px-4 py-3 bg-white text-gray-950 text-base font-normal rounded-xl border-2 border-gray-300 focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] shadow-sm transition-all"
                  style={{ color: '#16202B', fontWeight: 500 }}
                >
                  <option value="1.DEVIKOT_BHAKRANI_2">{formatPlantName("1.DEVIKOT_BHAKRANI_2")}</option>
                  <option value="3.DEVIKOT_RASALA_AG_3">{formatPlantName("3.DEVIKOT_RASALA_AG_3")}</option>
                  <option value="5.DEVIKOT_SADAK_KD_1">{formatPlantName("5.DEVIKOT_SADAK_KD_1")}</option>
                </select>
              </div>

              {/* Date mode selection */}
              <div className="space-y-2">
                <label className="block text-sm font-extrabold text-gray-950" style={{ color: '#16202B', fontWeight: 800 }}>
                  Date Mode
                </label>
                <select
                  value={scrapeDateMode}
                  onChange={(e) => setScrapeDateMode(e.target.value)}
                  className="w-full px-4 py-3 bg-white text-gray-950 text-base font-normal rounded-xl border-2 border-gray-300 focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] shadow-sm transition-all"
                  style={{ color: '#16202B', fontWeight: 500 }}
                >
                  <option value="current_date">Today</option>
                  <option value="yesterday_date">Yesterday</option>
                  <option value="manual">Specific Date</option>
                  <option value="loop_dates">Date Range</option>
                  <option value="last_month">Previous Month</option>
                </select>
              </div>

              {/* Conditional manual single date selection */}
              {scrapeDateMode === 'manual' && (
                <div className="space-y-2">
                  <label className="block text-sm font-extrabold text-gray-950" style={{ color: '#16202B', fontWeight: 800 }}>
                    Select Date
                  </label>
                  <input
                    type="date"
                    value={scrapeManualDate}
                    onChange={(e) => setScrapeManualDate(e.target.value)}
                    className="w-full px-4 py-3 bg-white text-gray-950 text-base font-normal rounded-xl border-2 border-gray-300 focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] shadow-sm transition-all"
                    style={{ color: '#16202B', fontWeight: 400 }}
                  />
                </div>
              )}

              {/* Conditional date range selection */}
              {scrapeDateMode === 'loop_dates' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-extrabold text-gray-950" style={{ color: '#16202B', fontWeight: 800 }}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={scrapeStartDate}
                      onChange={(e) => setScrapeStartDate(e.target.value)}
                      className="w-full px-4 py-3 bg-white text-gray-950 text-base font-normal rounded-xl border-2 border-gray-300 focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] shadow-sm transition-all"
                      style={{ color: '#16202B', fontWeight: 400 }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-extrabold text-gray-950" style={{ color: '#16202B', fontWeight: 800 }}>
                      End Date
                    </label>
                    <input
                      type="date"
                      value={scrapeEndDate}
                      onChange={(e) => setScrapeEndDate(e.target.value)}
                      className="w-full px-4 py-3 bg-white text-gray-950 text-base font-normal rounded-xl border-2 border-gray-300 focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] shadow-sm transition-all"
                      style={{ color: '#16202B', fontWeight: 400 }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Execution Progress */}
          {status.status !== 'idle' && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 mt-4">
              <div className="flex items-center justify-between mb-2 gap-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {status.status === 'running' ? (
                    <Loader2 size={14} className="text-[#0084CF] animate-spin flex-shrink-0" />
                  ) : status.status === 'completed' ? (
                    <CheckCircle size={14} className="text-[#1E7F4F] flex-shrink-0" />
                  ) : (
                    <XCircle size={14} className="text-[#B42318] flex-shrink-0" />
                  )}
                  <span className="text-xs font-extrabold text-gray-950 break-all" style={{ color: '#16202B', fontWeight: 800 }}>
                    {status.message || (status.status === 'running' ? 'Executing download...' : 'Operation complete')}
                  </span>
                </div>
                <span className="text-xs font-extrabold text-[#0084CF] flex-shrink-0" style={{ color: '#0084CF', fontWeight: 800 }}>
                  {status.progress || 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ 
                    width: status.status === 'completed' ? '100%' : `${status.progress || 0}%` 
                  }}
                  className={`h-full ${status.status === 'failed' ? 'bg-[#B42318]' : 'bg-[#0084CF]'}`}
                />
              </div>
            </div>
          )}

          {status.status === 'running' && activeTab === 'web_scrape' ? (
            <button
              onClick={onStopExecute}
              className="w-full py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm font-extrabold transition-all bg-[#D32F2F] text-white hover:bg-[#B71C1C] shadow-md hover:shadow-lg active:scale-[0.98]"
              style={{ fontWeight: 1000 }}
            >
              <XCircle size={18} />
              <span>STOP SCRAPING</span>
            </button>
          ) : (
            <button
              onClick={() => {
                if (activeTab === 'azure') {
                  onExecute();
                } else {
                  onExecuteWebScrape(selectedPlant, scrapeDateMode, scrapeManualDate, scrapeStartDate, scrapeEndDate);
                }
              }}
              disabled={executeDisabled}
              className={`w-full py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm font-extrabold transition-all group ${
                executeDisabled
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                  : 'bg-[#0084CF] text-white hover:bg-[#0066A3] shadow-md hover:shadow-lg active:scale-[0.98]'
              }`}
              style={{ fontWeight: 1000 }}
              title={actionsDisabled ? 'Only HO Admin can execute simulation tabs.' : !hasTokens ? `Insufficient tokens. Required: ${tokenCost}, Available: ${tokenBalance ?? 0}` : 'Download Raw Data'}
            >
              {status.status === 'running' ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Download size={18} className="group-hover:translate-y-0.5 transition-transform" />
              )}
              <span>
                {status.status === 'running' 
                  ? 'DOWNLOADING...' 
                  : activeTab === 'azure' 
                    ? 'DOWNLOAD RAW DATA' 
                    : 'DOWNLOAD WEBSCRAPED DATA'}
              </span>
              {status.status !== 'running' && (
                <span className="text-[10px] font-black px-2 py-0.5 rounded-md bg-white/20">
                  {tokenBalance === null ? 'UNLIMITED TOKENS' : `${tokenBalance ?? 0}/${tokenCost} TOKENS`}
                </span>
              )}
            </button>
          )}

          {/* Debug: Show selected date */}
          {activeTab === 'azure' ? (
            (dates.singleDate || dates.startDate) && (
              <div className="text-xs text-gray-900 mt-2 font-extrabold" style={{ color: '#16202B', fontWeight: 800 }}>
                Selected: {dateMode === 'single' ? dates.singleDate : dates.startDate}
              </div>
            )
          ) : (
            <div className="text-xs text-gray-900 mt-2 font-extrabold" style={{ color: '#16202B', fontWeight: 800 }}>
              Selected: {formatPlantName(selectedPlant)} ({
                scrapeDateMode === 'current_date' ? 'Today' :
                scrapeDateMode === 'yesterday_date' ? 'Yesterday' :
                scrapeDateMode === 'manual' ? 'Specific Date' :
                scrapeDateMode === 'loop_dates' ? 'Date Range' :
                scrapeDateMode === 'last_month' ? 'Previous Month' :
                scrapeDateMode
              })
            </div>
          )}
        </div>
      </motion.div>

      {/* Right Column - Download History */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full"
      >
        {/* History Header */}
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <Clock size={20} className="text-gray-600" />
            </div>
            <div>
              <h3 className="text-sm font-extrabold text-gray-950" style={{ color: '#16202B', fontWeight: 800 }}>Download History</h3>
              <p className="text-xs text-gray-900 font-extrabold" style={{ color: '#16202B', fontWeight: 800 }}>Recent downloads and their status</p>
            </div>
          </div>
        </div>

        {/* History Table */}
        <div className="flex-1 overflow-y-auto min-h-0 relative bg-white">
          <table className="w-full border-collapse">
            <thead className="bg-[#F2F8FC] border-b-2 border-gray-200">
              <tr style={{ height: '48px' }}>
                <th className="px-4 py-3 text-left text-xs font-black text-black uppercase tracking-wider" style={{ color: '#16202B', fontWeight: 900, backgroundColor: '#F2F8FC' }}>
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-black text-black uppercase tracking-wider" style={{ color: '#16202B', fontWeight: 900, backgroundColor: '#F2F8FC' }}>
                  Size
                </th>
                <th className="px-4 py-3 text-left text-xs font-black text-black uppercase tracking-wider" style={{ color: '#16202B', fontWeight: 900, backgroundColor: '#F2F8FC' }}>
                  Count
                </th>
                <th className="px-4 py-3 text-left text-xs font-black text-black uppercase tracking-wider" style={{ color: '#16202B', fontWeight: 900, backgroundColor: '#F2F8FC' }}>
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-black text-black uppercase tracking-wider" style={{ color: '#16202B', fontWeight: 900, backgroundColor: '#F2F8FC' }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredHistory.length > 0 ? (
                filteredHistory.map((item) => (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <Calendar size={12} className="text-gray-400" />
                        <span className="text-xs text-gray-950 font-extrabold" style={{ color: '#16202B', fontWeight: 800 }}>{item.dateDisplay}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-950 font-extrabold" style={{ color: '#16202B', fontWeight: 800 }}>
                      {item.size}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-950 font-extrabold" style={{ color: '#16202B', fontWeight: 800 }}>
                      {item.fileCount ?? 0}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${item.status === 'completed'
                        ? 'bg-green-50 text-[#1E7F4F]'
                        : 'bg-red-50 text-[#B42318]'
                        }`}>
                        {item.status === 'completed' ? (
                          <CheckCircle size={10} />
                        ) : (
                          <XCircle size={10} />
                        )}
                        {item.status === 'completed' ? 'Done' : 'Failed'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => onRedownload(item)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 transition-colors border border-gray-200"
                          title="Re-run Simulation"
                        >
                          <RefreshCw size={14} />
                        </button>
                        <button
                          onClick={() => onDeleteHistory && onDeleteHistory(item)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 transition-colors border border-red-200"
                          title="Delete History"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <FileArchive size={32} className="text-gray-300" />
                      <p className="text-sm text-gray-950 font-extrabold" style={{ color: '#16202B', fontWeight: 800 }}>No download history available</p>
                      <p className="text-xs text-gray-900 font-extrabold" style={{ color: '#16202B', fontWeight: 800 }}>Files will appear here after download</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default DownloadSection;
